<template>
    <form
        class="flex flex-col gap-y-4"
        @submit="handleSubmitCheckoutForm"
    >
        <billing-address
            v-model:editing="editingBillingAddress"
            :customer="null"
            :is-full-address="fullAddressRequired"
            :title="t('checkout.billingAddress')"
        />

        <checkout-section-separator
            v-if="requiresRecurringToken && readyForRecurringToken"
        />

        <recurring-information
            v-if="requiresRecurringToken && readyForRecurringToken"
            ref="recurringInformationRef"
            v-model:editing="editingRecurringInformation"
            :customer="primaryCustomer"
            :facility="facility"
            :recurring-total="recurringTotal"
            :payment-method-input="paymentMethodInput"
        />

        <checkout-section-separator v-if="readyForPayment" />

        <div v-else-if="!readyForPayment">
            <prime-button
                :label="
                    requiresPayment
                        ? t('checkout.continueToPayment')
                        : t('checkout.continueToTermsAndConditions')
                "
                type="submit"
                class="w-full"
                :loading="isValidating"
            />
        </div>

        <payment-information
            v-if="readyForPayment && requiresPayment"
            ref="paymentInformationRef"
            :customer="primaryCustomer"
            :facility="facility"
            :checkout-total="dueToday"
            :title="paymentTitle"
            :payment-method-input="paymentMethodInput"
        />

        <document-section
            v-if="readyForPayment"
            :contract="contract"
            :is-signing-document="isPending || isValidating"
            :balance-due-mode="false"
            :invalid-initials="invalidInitials"
            :invalid-signatures="invalidSignatures"
            @inline-checkout-document="handleInlineCheckoutDocument"
            @inline-checkout="handleInlineCheckout"
        />

        <div
            v-if="showPleaseFixMessage"
            class="text-red-600 mt-4 font-medium"
        >
            {{ t('checkout.pleaseFixErrorsInRedAbove') }}
        </div>
    </form>
</template>

<script setup lang="ts">
import { toTypedSchema } from '@vee-validate/yup'
import { object } from 'yup'
import { useForm } from 'vee-validate'
import { useRpQuery, UserReportableError } from '~/composables/graphql'
import { useMutation } from '@tanstack/vue-query'
import { useSelectedLanguage } from '~/composables/use-selected-language'
import type {
    CheckoutRecurringInput,
    CommitCheckoutInput,
    DocumentInput
} from '~/resources/graphql/graphql'
import { computed, nextTick, onMounted, ref, toRef, watch } from 'vue'
import { type FragmentType, graphql } from '~/resources/graphql'
import PrimeButton from 'primevue/button'
import { handleValidationErrors } from '~/components/PurchaseContract/DocumentSection/handle-validation-errors'
import { GraphQLRequestError } from 'awesome-graphql-client'

import {
    CheckoutFormCheckoutFragment,
    CheckoutFormQueryFragment,
    useCheckoutForm
} from '~/components/Checkout/Forms/use-checkout-form'

import BillingAddress, {
    BillingAddressInitialValues,
    BillingAddressSchema
} from '~/components/Checkout/Sections/BillingAddress.vue'
import RecurringInformation, {
    RecurringInformationInitialValues,
    RecurringInformationSchema
} from '~/components/Checkout/Sections/RecurringInformation.vue'
import PaymentInformation, {
    PaymentInformationInitialValues,
    PaymentInformationSchema,
    type PaymentInformationSchemaType
} from '~/components/Checkout/Sections/PaymentInformation.vue'
import DocumentSection, {
    DocumentSectionContractFragment
} from '~/components/PurchaseContract/DocumentSection/DocumentSection.vue'
import { useShowAlert } from '#imports'
import CheckoutSectionSeparator from '~/components/Checkout/Forms/CheckoutSectionSeparator.vue'
import {
    checkoutPaymentInput,
    checkoutRecurringInput,
    resetPaymentErrors
} from '~/components/Checkout/Forms/checkout-helpers'
import { useFingerprintCheckoutEventPayload } from '~/composables/use-fingerprint-visitor-id'
import { CheckoutSuccessKioskDisplayFragment } from '~/components/Checkout/CheckoutSuccessKioskDisplay.vue'
import {
    PaymentMethodInputPlanContractFragment,
    useGuestPaymentMethodInput
} from '~/components/Checkout/Sections/use-payment-method-input'
import { useI18n } from 'vue-i18n'

const props = defineProps<{
    query: FragmentType<typeof CheckoutFormQueryFragment>
    checkout: FragmentType<typeof CheckoutFormCheckoutFragment>
    contract: FragmentType<typeof DocumentSectionContractFragment> &
        FragmentType<typeof PaymentMethodInputPlanContractFragment>
}>()

const emit = defineEmits<{
    (
        e: 'kioskCheckoutSuccess',
        payload: FragmentType<typeof CheckoutSuccessKioskDisplayFragment>
    ): void
}>()
const { t } = useI18n({ useScope: 'global' })

const {
    checkoutId,

    primaryCustomer,
    facility,

    fullAddressRequired,
    requiresCustomerInformation,

    editingBillingAddress,
    editingRecurringInformation,

    dueToday,
    recurringTotal,
    requiresRecurringToken,

    incrementProcessing,
    decrementProcessing
} = useCheckoutForm(toRef(props, 'query'), toRef(props, 'checkout'))
editingBillingAddress.value = true

const schema = toTypedSchema(
    object({
        address: BillingAddressSchema(t),
        recurring: RecurringInformationSchema(t),
        payment: PaymentInformationSchema(t)
    })
)

const {
    handleSubmit,
    errors,
    validateField,
    validate,
    setErrors,
    setFieldError,
    values
} = useForm({
    validationSchema: schema,
    initialValues: {
        address: BillingAddressInitialValues(null, fullAddressRequired.value),
        recurring: RecurringInformationInitialValues(
            requiresRecurringToken.value ? recurringTotal.value : null
        ),
        payment: PaymentInformationInitialValues(dueToday.value)
    }
})

const showPleaseFixMessage = computed(() => {
    // No submission yet means don't show the error message
    if (submitCount.value < 1) {
        return false
    }

    // If we are on the pay now portion, show all errors
    if (readyForPayment.value) {
        return Object.keys(errors.value).length > 0
    }

    // Otherwise show errors with payment filtered out
    return (
        Object.keys(errors.value).filter(field => !field.startsWith('payment'))
            .length > 0
    )
})

const pendingCustomerInformation = ref(requiresCustomerInformation.value)
const readyForRecurringToken = computed(
    () => pendingCustomerInformation.value === false
)
watch(editingBillingAddress, (newValue, oldValue) => {
    // If we require customer information for the payment methods, when the
    // customer information changes, we have to make a new session, so reset the form
    if (requiresCustomerInformation.value) {
        if (oldValue === false && newValue === true) {
            pendingCustomerInformation.value = true
        }
    }
})

const { paymentMethodInput, address } = useGuestPaymentMethodInput(
    toRef(props, 'contract')
)
onMounted(() => {
    if (requiresCustomerInformation.value) {
        if (address.value.postal !== '') {
            pendingCustomerInformation.value = false
        }
    }
})

const pendingRecurringToken = ref(requiresRecurringToken.value)
const readyForPayment = computed(
    () =>
        pendingCustomerInformation.value === false &&
        pendingRecurringToken.value === false
)
const requiresPayment = computed(() => dueToday.value.isNotZero())
const isValidating = ref(false)
// We use our own submit count because the continue to payment button does not submit the vee validate form
const submitCount = ref(0)
const paymentTitle = computed(() => {
    if (requiresRecurringToken.value) {
        return t('checkout.paymentDueToday')
    } else {
        return t('payment')
    }
})

// When editing the recurring information section, reset the form value
watch(editingRecurringInformation, (newValue, oldValue) => {
    if (oldValue === false && newValue === true) {
        pendingRecurringToken.value = true
    }
})

// References to components allowing us to submit them
const recurringInformationRef = ref<InstanceType<typeof RecurringInformation>>()
const paymentInformationRef = ref<InstanceType<typeof PaymentInformation>>()
const preservedWindcaveSessionPaymentMethod =
    ref<PaymentInformationSchemaType['paymentMethod']>(null)

// Main submit function
async function handleSubmitCheckoutForm(event?: SubmitEvent) {
    // Prevent form action
    event?.preventDefault()
    submitCount.value += 1

    isValidating.value = true
    try {
        // Just get the customer information since the payment processor wants
        // this up front
        if (pendingCustomerInformation.value) {
            // Otherwise, validate the entire form
            const result = await validate()
            const isValid = Object.entries(result.results).every(
                ([key, value]) => {
                    return (
                        key.startsWith('payment') ||
                        key.startsWith('recurring') ||
                        value.valid
                    )
                }
            )

            // Also clear this error so we don't flash red on the payment error
            resetPaymentErrors(setErrors)
            // TODO: Do we need to do this? Also clear this error so we don't flash red on the recurring error
            setFieldError('recurring', undefined)

            // Ready for recurring token or payment!
            if (isValid) {
                submitCount.value = 0 // reset submit count
                editingBillingAddress.value = false
                pendingCustomerInformation.value = false

                // Recurring payment method required?
                if (requiresRecurringToken.value) {
                    pendingRecurringToken.value = true
                    editingRecurringInformation.value = true
                }
            } else {
                // TODO: Find a way to scroll up
            }

            return
        }

        if (pendingRecurringToken.value) {
            // Recurring checkout

            // If recurring field is not valid, submit stripe form
            // We use silent so we don't show the error message until after stripe form is submitted
            const { valid: validRecurring } = await validateField('recurring', {
                mode: 'silent'
            })
            if (!validRecurring) {
                await recurringInformationRef.value.submitRecurringForm()
            }

            // Otherwise, validate the entire form
            const result = await validate()
            const isValid = Object.entries(result.results).every(
                ([key, value]) => {
                    return key.startsWith('payment') || value.valid
                }
            )

            // Also clear this error so we don't flash red on the payment error
            resetPaymentErrors(setErrors)

            if (isValid) {
                // We don't submit the form here for $0 transactions because we go
                // to the document section next

                // Close all the other pieces and we are ready for payment
                submitCount.value = 0 // reset submit count
                editingBillingAddress.value = false
                pendingRecurringToken.value = false
                editingRecurringInformation.value = false
            } else {
                // TODO: Find a way to scroll up
            }

            return
        }

        // Otherwise, validate the entire form
        const result = await validate()
        const isValid = Object.entries(result.results).every(([key, value]) => {
            return key.startsWith('payment') || value.valid
        })

        if (isValid) {
            // A completed Windcave session must not be authorized again when
            // document or form validation sends the kiosk checkout through another submit.
            const selectedPaymentMethod = values.payment.paymentMethod
            const hasPreservedWindcaveSession =
                preservedWindcaveSessionPaymentMethod.value !== null &&
                selectedPaymentMethod?.type === 'PaymentMethod' &&
                selectedPaymentMethod.token ===
                    preservedWindcaveSessionPaymentMethod.value.token

            if (
                !result.results.payment?.valid &&
                !hasPreservedWindcaveSession
            ) {
                // Also clear this error so we don't flash red on the payment error
                // onsubmit will validate the whole form again
                resetPaymentErrors(setErrors)

                const submittingWindcavePayment =
                    paymentInformationRef.value.shouldPreflightBeforeSubmittingPaymentForm()
                await paymentInformationRef.value.submitPaymentForm()

                if (submittingWindcavePayment) {
                    // Wait for the emitted Windcave token to reach vee-validate
                    // before retaining the exact session that was already authorized.
                    await nextTick()
                    const submittedPaymentMethod = values.payment.paymentMethod
                    if (
                        submittedPaymentMethod?.type === 'PaymentMethod' &&
                        submittedPaymentMethod.token?.startsWith('WC|') === true
                    ) {
                        preservedWindcaveSessionPaymentMethod.value = {
                            ...submittedPaymentMethod
                        }
                    }
                }
            }

            return onSubmit(event)
        } else {
            // TODO: Find a way to scroll up
        }
    } finally {
        isValidating.value = false
    }
}

const invalidInitials = ref(new Set<number>())
const invalidSignatures = ref(new Set<number>())

const rpQuery = useRpQuery()
const selectedLanguage = useSelectedLanguage()
const { mutate, isPending } = useMutation({
    mutationFn: async (mutationData: CommitCheckoutInput) => {
        const response = await rpQuery(
            graphql(/** GraphQL */ `
                mutation CommitInlineCheckout(
                    $input: CommitInlineCheckoutInput!
                    $language: Language!
                ) {
                    commitInlineCheckout(input: $input) {
                        __typename
                        ... on CommitCheckoutResult {
                            ...CheckoutSuccessKioskDisplayFragment
                        }
                        ... on ValidationError {
                            fieldErrors {
                                path
                                message
                            }
                        }
                    }
                }
            `),
            {
                input: mutationData,
                language: selectedLanguage.value
            }
        )

        if (response.commitInlineCheckout === null) {
            throw new Error(t('checkout.inlineCheckoutError', { code: 'CFKG' }))
        }

        if (
            response.commitInlineCheckout.__typename ===
                'CommitCheckoutResult' ||
            response.commitInlineCheckout.__typename === 'ValidationError'
        ) {
            return response.commitInlineCheckout
        }

        throw new Error()
    }
})

// Once form is validated, this preps and calls the mutation
const showAlert = useShowAlert()
const savedDocumentInput = ref()
const { getFingerprintCheckoutEventPayload } =
    useFingerprintCheckoutEventPayload()
const onSubmit = handleSubmit(async values => {
    // Customer and Address
    const customer = {
        address: {
            ...values.address,
            // Wipe out the full address
            fullAddress: undefined
        }
    }

    // Recurring Token
    let recurring: CheckoutRecurringInput | null = null
    if (requiresRecurringToken.value) {
        recurring = checkoutRecurringInput(values.recurring)
    }

    // Payment Items
    const payment = checkoutPaymentInput(values.payment)

    incrementProcessing()

    // Attach Fingerprint event context only to the checkout commit mutation
    // that needs fraud review context.
    const fingerprintEventPayload = await getFingerprintCheckoutEventPayload()

    mutate(
        {
            checkoutId: checkoutId.value,
            customer,
            recurring,
            payment,
            document: savedDocumentInput.value,
            fingerprintEventPayload
        },
        {
            onSuccess: data => {
                if (data.__typename === 'ValidationError') {
                    handleValidationErrors(
                        data.fieldErrors,
                        invalidInitials,
                        invalidSignatures
                    )

                    let messageBuilder = ''
                    data.fieldErrors.forEach(error => {
                        if (
                            error.path !== 'initials' &&
                            error.path !== 'signatures'
                        ) {
                            messageBuilder += error.message
                        }
                    })
                    if (messageBuilder !== '') {
                        showAlert(t('headsUp'), messageBuilder)
                    }
                } else {
                    emit('kioskCheckoutSuccess', data)
                }
            },
            onError(error) {
                if (
                    !(error instanceof UserReportableError) &&
                    !(error instanceof GraphQLRequestError)
                ) {
                    showAlert(t('headsUp'), String(error))
                }
            },
            onSettled() {
                decrementProcessing()
            }
        }
    )
})

function handleInlineCheckoutDocument(documentInput: DocumentInput) {
    savedDocumentInput.value = documentInput
}

function handleInlineCheckout(documentInput: DocumentInput) {
    savedDocumentInput.value = documentInput
    handleSubmitCheckoutForm().catch(error => {
        showAlert(t('headsUp'), String(error))
    })
}
</script>
